import { useState, useEffect, useCallback } from 'react';
import {
  createBonReception,
  saveBonReception,
  getBonReception,
  listBonReceptions,
  getItemsInBonReception,
  validateBonReception,
  unvalidateBonReception,
  transferReceptionToFacture,
  listDBFFiles,
  importFromDBF,
  uploadBonReceptionDBF,
  uploadBonReceptionDetailsDBF,
  uploadBothBonReceptionDBF,
} from '../models/achatReceptionAPI';

export const useFetchAllBonReceptions = (params) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try { setLoading(true); const res = await listBonReceptions(params); setItems(res.data.items || []); setTotal(res.data.total || 0); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { items, total, loading, error, refetch: fetchData };
};

export const useFetchBonReceptionById = (id) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchOne = useCallback(async () => {
    if (!id) return;
    try { setLoading(true); const res = await getBonReception(id); setData(res.data); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchOne(); }, [fetchOne]);
  return { data, loading, error, refetch: fetchOne };
};

export const useFetchItemsInBonReception = (id) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchItems = useCallback(async () => {
    if (!id) return;
    try { setLoading(true); const res = await getItemsInBonReception(id); setItems(res.data || []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  return { items, loading, error, refetch: fetchItems };
};

export const useCreateBonReception = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const create = async (payload) => {
    try { setLoading(true); const res = await createBonReception(payload); return res.data; }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { create, loading, error };
};

export const useSaveBonReception = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const save = async (bonReception, items) => {
    try { setLoading(true); const res = await saveBonReception({ bonReception, items }); return res.data; }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { save, loading, error };
};

export const useValidateBonReception = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const validate = async (id) => {
    try { setLoading(true); const res = await validateBonReception(id); return res.data; }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { validate, loading, error };
};

export const useUnvalidateBonReception = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const unvalidate = async (id) => {
    try { setLoading(true); const res = await unvalidateBonReception(id); return res.data; }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { unvalidate, loading, error };
};

export const useTransferReceptionToFacture = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const transfer = async (id) => {
    try { setLoading(true); const res = await transferReceptionToFacture(id); return res.data; }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { transfer, loading, error };
};

export const useListDBFFiles = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await listDBFFiles();
      setFiles(res.data || []);
      return res.data;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { files, fetchFiles, loading, error };
};

export const useImportFromDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const importData = async (fileName) => {
    try {
      setLoading(true);
      setError('');
      const res = await importFromDBF(fileName);
      return res.data;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { importData, loading, error };
};

export const useUploadBonReceptionDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleUpload = async (file) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await uploadBonReceptionDBF(formData);
      setUploadResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during upload';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { handleUpload, loading, error, uploadResult };
};

export const useUploadBonReceptionDetailsDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleUpload = async (file) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await uploadBonReceptionDetailsDBF(formData);
      setUploadResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during upload';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { handleUpload, loading, error, uploadResult };
};

export const useUploadBothBonReceptionDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleUpload = async (bonreceptionFile, detailsFile) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('bonreceptionFile', bonreceptionFile);
    formData.append('detailsFile', detailsFile);

    try {
      const response = await uploadBothBonReceptionDBF(formData);
      setUploadResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during upload';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { handleUpload, loading, error, uploadResult };
};
